import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faCopy,
  faDatabase,
  faDownload,
  faEraser,
  faExclamationTriangle,
  faListAlt,
  faPlay,
  faSearch,
  faShieldAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { listSavedQueries, runSelectQuery } from "../services/queriesApi";

const DEFAULT_LIMIT = 1000;
const DEFAULT_PER_PAGE = 20;
const PER_PAGE_OPTIONS = [10, 20, 50, 100, 200];
const LIMIT_OPTIONS = [100, 500, 1000, 2000, 5000];
const QUERY_STORAGE_KEY = "queries.prev_query_v2";
const LEGACY_QUERY_STORAGE_KEY = "prev_query";
const DEFAULT_QUERY = "SELECT *\nFROM backend_log\nORDER BY tbl_id DESC\nLIMIT 20";

const maskQuotedLiterals = (sql) => {
  let masked = "";
  let quote = null;
  let escaped = false;

  for (let i = 0; i < sql.length; i += 1) {
    const char = sql[i];

    if (quote) {
      masked += " ";

      if (quote === "`") {
        if (char === "`") quote = null;
        continue;
      }

      if (escaped) {
        escaped = false;
        continue;
      }

      if (char === "\\") {
        escaped = true;
        continue;
      }

      if (char === quote) {
        if (i + 1 < sql.length && sql[i + 1] === quote) {
          masked += " ";
          i += 1;
          continue;
        }

        quote = null;
      }

      continue;
    }

    if (char === "'" || char === '"' || char === "`") {
      quote = char;
      masked += " ";
      continue;
    }

    masked += char;
  }

  return { masked, balanced: quote === null };
};

const stripSingleTrailingSemicolon = (sql) => {
  const trimmed = sql.trim();
  return trimmed.endsWith(";") ? trimmed.slice(0, -1).trimEnd() : trimmed;
};

const validateSelectOnly = (sql) => {
  const trimmed = sql.trim();
  if (!trimmed) {
    return { ok: false, message: "Query is required.", query: "" };
  }

  const query = stripSingleTrailingSemicolon(trimmed);
  const { masked, balanced } = maskQuotedLiterals(query);

  if (!balanced) {
    return {
      ok: false,
      message: "Query has an unclosed quoted string or identifier.",
      query,
    };
  }

  if (!/^\s*select\b/i.test(masked)) {
    return { ok: false, message: "Only SELECT queries are allowed.", query };
  }

  if (masked.includes(";")) {
    return {
      ok: false,
      message: "Multiple SQL statements are not allowed.",
      query,
    };
  }

  if (/\/\*|\*\/|--[ \t\r\n]|#[^\r\n]*/.test(masked)) {
    return {
      ok: false,
      message: "SQL comments are not allowed in this module.",
      query,
    };
  }

  if (masked.includes(":=")) {
    return {
      ok: false,
      message: "Session variable assignment is not allowed.",
      query,
    };
  }

  const blockedPatterns = [
    /\b(insert|update|delete|replace|drop|truncate|alter|create|rename|grant|revoke|call|exec|execute|prepare|deallocate|set|use|show|describe|analyze|optimize|repair|handler|lock|unlock|commit|rollback|savepoint|kill|procedure)\b/i,
    /\b(start\s+transaction|load\s+data)\b/i,
    /\binto\s+(outfile|dumpfile|@)/i,
    /\b(load_file|sleep|benchmark|get_lock|release_lock|sys_exec|sys_eval)\s*\(/i,
    /\bfor\s+update\b/i,
    /\block\s+in\s+share\s+mode\b/i,
    /\bmysql\./i,
  ];

  if (blockedPatterns.some((pattern) => pattern.test(masked))) {
    return {
      ok: false,
      message: "Query contains a blocked SQL keyword or function.",
      query,
    };
  }

  if (/`?mysql`?\s*\./i.test(query)) {
    return {
      ok: false,
      message: "Query contains a blocked system schema.",
      query,
    };
  }

  return { ok: true, message: "", query };
};

const getSavedQuerySql = (row) => {
  const preferredKeys = [
    "query",
    "queries",
    "sql",
    "statement",
    "query_text",
    "q",
  ];

  for (const key of preferredKeys) {
    const match = Object.keys(row).find(
      (rowKey) => rowKey.toLowerCase() === key
    );
    if (match && String(row[match] || "").trim()) {
      return String(row[match]).trim();
    }
  }

  return "";
};

const getSavedQueryLabel = (row, index) => {
  const preferredKeys = [
    "name",
    "title",
    "label",
    "description",
    "query_name",
    "query_title",
  ];

  for (const key of preferredKeys) {
    const match = Object.keys(row).find(
      (rowKey) => rowKey.toLowerCase() === key
    );
    if (match && String(row[match] || "").trim()) {
      return String(row[match]).trim();
    }
  }

  const sql = getSavedQuerySql(row);
  return sql ? sql.replace(/\s+/g, " ").slice(0, 80) : `Saved Query ${index + 1}`;
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadCsv = (columns, rows) => {
  const csv = [
    columns.map(escapeCsvValue).join(","),
    ...rows.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(",")),
  ].join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `queries-result-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const QueriesPlaceholderPage = () => {
  const [query, setQuery] = useState(() => {
    return (
      localStorage.getItem(QUERY_STORAGE_KEY) ||
      localStorage.getItem(LEGACY_QUERY_STORAGE_KEY) ||
      DEFAULT_QUERY
    );
  });
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [savedRows, setSavedRows] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedSavedIndex, setSelectedSavedIndex] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const clientValidation = useMemo(() => validateSelectOnly(query), [query]);
  const columns = result?.columns || [];
  const rows = result?.rows || [];

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      columns.some((column) =>
        String(row[column] ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [columns, rows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  useEffect(() => {
    let active = true;
    setSavedLoading(true);

    listSavedQueries()
      .then((response) => {
        if (!active) return;
        setSavedRows(response.data?.data?.rows || []);
      })
      .catch(() => {
        if (!active) return;
        setSavedRows([]);
      })
      .finally(() => {
        if (active) setSavedLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, perPage, result]);

  const handleRun = async () => {
    const validation = validateSelectOnly(query);
    if (!validation.ok) {
      setError(validation.message);
      toast.error(validation.message);
      return;
    }

    setLoading(true);
    setError("");
    localStorage.setItem(QUERY_STORAGE_KEY, validation.query);
    localStorage.setItem(LEGACY_QUERY_STORAGE_KEY, validation.query);

    try {
      const response = await runSelectQuery(validation.query, limit);
      setResult(response.data?.data || null);
      toast.success("Query executed successfully.");
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        requestError.message ||
        "Unable to run query.";
      setError(message);
      setResult(null);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSavedQueryChange = (event) => {
    const index = event.target.value;
    setSelectedSavedIndex(index);

    if (index === "") return;

    const row = savedRows[Number(index)];
    const savedQuery = row ? getSavedQuerySql(row) : "";
    if (savedQuery) {
      setQuery(savedQuery);
    }
  };

  const handleCopyQuery = async () => {
    try {
      await navigator.clipboard.writeText(query);
      toast.success("Query copied.");
    } catch (copyError) {
      toast.error("Unable to copy query.");
    }
  };

  const handleClear = () => {
    setQuery("");
    setResult(null);
    setError("");
    setSearch("");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-xl font-bold text-gray-900">
                  Queries
                </h1>
                <p className="text-xs text-gray-500">
                  Read-only SELECT query runner using the restricted queries database user
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                <FontAwesomeIcon icon={faShieldAlt} />
                SELECT only
              </span>
              <span className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
                <FontAwesomeIcon icon={faCheckCircle} />
                Ctrl+Enter to run
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <section className="rounded-md border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-sm font-bold text-gray-900">SQL Editor</h2>
                  <p className="text-xs text-gray-500">
                    Only one read-only SELECT statement is accepted.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-gray-600" htmlFor="query-limit">
                    Max rows
                  </label>
                  <select
                    id="query-limit"
                    value={limit}
                    onChange={(event) => setLimit(Number(event.target.value))}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {LIMIT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700" htmlFor="saved-query">
                  Saved Query List
                </label>
                <select
                  id="saved-query"
                  value={selectedSavedIndex}
                  onChange={handleSavedQueryChange}
                  disabled={savedLoading || savedRows.length === 0}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">
                    {savedLoading
                      ? "Loading saved queries..."
                      : savedRows.length === 0
                      ? "No saved queries available"
                      : "Select saved query"}
                  </option>
                  {savedRows.map((row, index) => (
                    <option
                      key={`${getSavedQueryLabel(row, index)}-${index}`}
                      value={index}
                      disabled={!getSavedQuerySql(row)}
                    >
                      {getSavedQueryLabel(row, index)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label className="text-xs font-semibold text-gray-700" htmlFor="query-textarea">
                    Query
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold ${
                      clientValidation.ok
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-red-50 text-red-700 ring-1 ring-red-200"
                    }`}
                  >
                    <FontAwesomeIcon icon={clientValidation.ok ? faCheckCircle : faExclamationTriangle} />
                    {clientValidation.ok ? "Allowed" : "Blocked"}
                  </span>
                </div>
                <textarea
                  id="query-textarea"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                      event.preventDefault();
                      handleRun();
                    }
                  }}
                  rows={18}
                  spellCheck={false}
                  className="min-h-[360px] w-full resize-y rounded-md border border-gray-300 bg-slate-950 px-4 py-3 font-mono text-sm leading-6 text-slate-100 shadow-inner outline-none transition placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  placeholder="SELECT * FROM table_name LIMIT 100"
                />
                {!clientValidation.ok && (
                  <p className="mt-2 text-xs font-medium text-red-700">
                    {clientValidation.message}
                  </p>
                )}
              </div>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-red-600" />
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={loading || !clientValidation.ok}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faPlay} className={loading ? "animate-spin" : ""} />
                  Run
                </button>
                <button
                  type="button"
                  onClick={handleCopyQuery}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  Copy
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faEraser} />
                  Clear
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-md border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Results</h2>
                  <p className="text-xs text-gray-500">
                    {result
                      ? `${Number(result.row_count || 0).toLocaleString()} row(s) in ${Number(result.duration_ms || 0).toLocaleString()} ms`
                      : "Run a SELECT query to display results."}
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400"
                    />
                    <input
                      type="text"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search results"
                      disabled={rows.length === 0}
                      className="w-full rounded-md border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 sm:w-56"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadCsv(columns, filteredRows)}
                    disabled={columns.length === 0}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    CSV
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-[520px] flex-col items-center justify-center gap-3 text-gray-600">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl text-emerald-600 animate-spin" />
                <p className="text-sm font-semibold">Running query...</p>
              </div>
            ) : columns.length === 0 ? (
              <div className="flex min-h-[520px] flex-col items-center justify-center gap-3 px-5 text-center text-gray-500">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                  <FontAwesomeIcon icon={faListAlt} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    No result set to display
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Empty SELECT results will show here once columns are returned.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="max-h-[590px] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-100">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column}
                            className="whitespace-nowrap px-3 py-2 text-xs font-bold uppercase text-gray-600"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {paginatedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="px-3 py-8 text-center text-sm text-gray-500"
                          >
                            No matching records found.
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-emerald-50/40">
                            {columns.map((column) => (
                              <td
                                key={`${rowIndex}-${column}`}
                                className="max-w-[320px] whitespace-pre-wrap break-words px-3 py-2 align-top font-mono text-xs text-gray-800"
                              >
                                {row[column] === null || row[column] === undefined
                                  ? ""
                                  : String(row[column])}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-gray-500">
                    Showing {filteredRows.length === 0 ? 0 : (currentPage - 1) * perPage + 1}
                    {" - "}
                    {Math.min(currentPage * perPage, filteredRows.length)}
                    {" of "}
                    {filteredRows.length.toLocaleString()} row(s)
                    {result?.limit_applied ? `, max ${Number(result.limit || limit).toLocaleString()} rows applied` : ""}
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={perPage}
                      onChange={(event) => setPerPage(Number(event.target.value))}
                      className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    >
                      {PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} / page
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={currentPage <= 1}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="min-w-[72px] text-center text-xs font-semibold text-gray-700">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      disabled={currentPage >= totalPages}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QueriesPlaceholderPage;
